import React, { useEffect, useState } from "react";
import { getClientNiche, getLikedKtruStats, getSearchStats } from "../../api/paidClientsService";
import useClientData from "../../hooks/useClientData";
import { fmtMoney } from "../../utils/format";
import { InlineText, TabState } from "../common";

function DeclaredNiche({ value, onSave }) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <InlineText
        value={value}
        maxLength={200}
        placeholder="Ниша со слов клиента"
        onSave={async (v) => {
          await onSave(v);
          setEditing(false);
        }}
      />
    );
  }
  return (
    <>
      <div style={{ marginBottom: 8 }}>
        {value ? <b>{value}</b> : <span className="muted">не указана — спросить на звонке</span>}
      </div>
      <button type="button" className="pc-btn sm" onClick={() => setEditing(true)}>
        ✏️ Изменить
      </button>
    </>
  );
}

function InterestDetails({ userId }) {
  const [ktru, setKtru] = useState(null);
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    Promise.allSettled([getLikedKtruStats(userId), getSearchStats(userId)]).then(([k, s]) => {
      if (ignore) return;
      setKtru(k.status === "fulfilled" ? k.value : { error: true });
      setSearch(s.status === "fulfilled" ? s.value : { error: true });
      setLoading(false);
    });
    return () => {
      ignore = true;
    };
  }, [userId]);

  if (loading) return <div className="muted">Загрузка…</div>;

  const renderState = (d) => {
    if (d?.error) return <div className="muted">Не удалось загрузить</div>;
    if (d && d.isCalculated === false) return <div className="muted">Данные ещё подсчитываются</div>;
    return null;
  };

  return (
    <>
      <div className="muted small" style={{ margin: "4px 0 6px" }}>
        Детальные лайкнутые КТРУ
      </div>
      {renderState(ktru) || (
        <table className="pc-table small">
          <thead>
            <tr>
              <th>Код КТРУ</th>
              <th>Название товара</th>
              <th className="r">Лайкнуто лотов</th>
            </tr>
          </thead>
          <tbody>
            {ktru?.data?.length ? (
              ktru.data.map((r, i) => (
                <tr key={`${r.truCode}-${i}`}>
                  <td className="mono">{r.truCode}</td>
                  <td>{r.truName || "Название не найдено"}</td>
                  <td className="r">{r.count}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="muted">
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div className="muted small" style={{ margin: "14px 0 6px" }}>
        Частые поисковые запросы клиента
      </div>
      {renderState(search) || (
        <table className="pc-table small">
          <thead>
            <tr>
              <th>Поисковая фраза</th>
              <th className="r">Число поисков</th>
            </tr>
          </thead>
          <tbody>
            {search?.data?.length ? (
              search.data.map((r, i) => (
                <tr key={`${r.query}-${i}`}>
                  <td>{r.query}</td>
                  <td className="r">{r.count}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="muted">
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

const groupName = (g) => g.name || g.group;

const NicheTab = ({ userId, detail, patch, openUserDialog }) => {
  const { data, setData, loading, error } = useClientData(getClientNiche, userId);
  const hasBin = (detail.passport?.bins || []).length > 0;
  const [showDetails, setShowDetails] = useState(!hasBin);

  useEffect(() => {
    setShowDetails(!hasBin);
  }, [hasBin, userId]);

  const fact = data?.fact || [];
  const interest = data?.interest || [];
  const mismatch = !!data?.mismatch && fact[0] && interest[0];

  const saveDeclared = async (declaredNiche) => {
    if (await patch({ declaredNiche }, "Ниша со слов сохранена"))
      setData((prev) => ({ ...(prev || {}), declared: declaredNiche }));
  };

  return (
    <TabState loading={loading} error={error} data={data}>
      <div className="pc-card">
      <h3>Ниша с трёх сторон</h3>
      {!hasBin && (
        <div className="alert info">
          БИН не указан — определяем нишу клиента по его лайкам и подпискам КТРУ
        </div>
      )}
      <div className="pc-three">
        <div className="col">
          <p>По факту — заявки и договоры</p>
          {!hasBin ? (
            <span className="muted small">БИН не указан</span>
          ) : !fact.length ? (
            <span className="muted small">По указанному БИН нет заявок за 24 мес.</span>
          ) : (
            <ol>
              {fact.slice(0, 5).map((g) => (
                <li key={g.group}>
                  <span className="mono small">{g.group}</span>
                  {g.name && ` — ${g.name}`}
                  <div className="muted small">
                    {/* sum = 0 — топ по числу заявок */}
                    {g.sum ? `${fmtMoney(g.sum)} · ` : ""}
                    {g.count} шт.
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className={`col ${!hasBin ? "main" : ""}`}>
          <p>По интересу — лайки и подписки</p>
          {!interest.length ? (
            <span className="muted small">нет данных</span>
          ) : (
            <ol>
              {interest.slice(0, 5).map((g) => (
                <li key={g.group}>
                  <span className="mono small">{g.group}</span>
                  {g.name && ` — ${g.name}`}
                  <div>
                    <span className="tag violet">{g.likes} баллов интереса</span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="col">
          <p>Со слов клиента</p>
          <DeclaredNiche value={data?.declared} onSave={saveDeclared} />
        </div>
      </div>
      {mismatch && (
        <div className="alert">
          Выигрывает в одной нише ({groupName(fact[0])}), а ищет в другой ({groupName(interest[0])}) —
          повод позвонить и перенастроить подписку.
        </div>
      )}
      </div>

      <div className="pc-card">
        <div className="pc-row" style={{ justifyContent: "space-between" }}>
          <button type="button" className="pc-btn link" onClick={() => setShowDetails((v) => !v)}>
            {showDetails ? "▾" : "▸"} Подробная разбивка интересов (точные КТРУ лайков и поисковые слова)
          </button>
          <button type="button" className="pc-btn sm" onClick={() => openUserDialog("likes")}>
            Лайкнутые лоты
          </button>
        </div>
        {showDetails && (
          <div style={{ marginTop: 10 }}>
            <InterestDetails userId={userId} />
          </div>
        )}
      </div>
    </TabState>
  );
};

export default NicheTab;
